import { SELF_PERSPECTIVE, SELF_PERSPECTIVE_KEY, type Perspective } from './perspective'

export type AnalysisPerspectiveRef = {
  readonly perspective?: Perspective | null
  readonly key?: string
}

export type PerspectiveAverageEntry = AnalysisPerspectiveRef & {
  readonly average: number | null
  readonly numberOfFeedbacks?: number
}

export type ValueBarPerspectiveEntry = {
  readonly perspective: Perspective
  readonly average: number | null
  readonly numberOfFeedbacks: number
}

export const analysisPerspectiveKey = (entry: AnalysisPerspectiveRef): string | undefined => {
  return entry.perspective?.key ?? entry.key
}

export const resolveAnalysisPerspective = (
  entry: AnalysisPerspectiveRef,
  topicPerspectives: ReadonlyArray<Perspective> = [],
): Perspective | undefined => {
  if (entry.perspective) {
    return entry.perspective
  }
  const key = entry.key
  if (!key) {
    return undefined
  }
  if (key === SELF_PERSPECTIVE_KEY) {
    return SELF_PERSPECTIVE
  }
  return topicPerspectives.find((p) => p.key === key)
}

/** Join analysis averages onto topic Perspective objects by key. */
export const toValueBarPerspectives = (
  analysis: ReadonlyArray<{
    perspective?: Perspective | null
    key?: string
    average: number | null
    numberOfFeedbacks: number
  }>,
  topicPerspectives: ReadonlyArray<Perspective>,
): ValueBarPerspectiveEntry[] => {
  return analysis.flatMap((entry) => {
    const perspective = resolveAnalysisPerspective(entry, topicPerspectives)
    if (!perspective) {
      return []
    }
    return [
      {
        perspective,
        average: entry.average,
        numberOfFeedbacks: entry.numberOfFeedbacks,
      },
    ]
  })
}

export const findPerspectiveEntry = <T extends AnalysisPerspectiveRef>(
  entries: ReadonlyArray<T>,
  key: string,
): T | undefined => {
  return entries.find((entry) => analysisPerspectiveKey(entry) === key)
}

export const selfEntry = <T extends AnalysisPerspectiveRef>(
  entries: ReadonlyArray<T>,
): T | undefined => {
  return findPerspectiveEntry(entries, SELF_PERSPECTIVE_KEY)
}

export const nonSelfEntries = <T extends AnalysisPerspectiveRef>(entries: ReadonlyArray<T>): T[] => {
  return entries.filter((entry) => analysisPerspectiveKey(entry) !== SELF_PERSPECTIVE_KEY)
}

export const hasResponses = (entry: {
  numberOfFeedbacks?: number
  average?: number | null
}): boolean => {
  if (entry.numberOfFeedbacks != null) {
    return entry.numberOfFeedbacks > 0
  }
  return entry.average != null
}

/**
 * Equal-weight mean of non-self perspective averages that have data.
 * Zero-response entries (average: null) are skipped.
 */
export const collapseNonSelfAverage = (
  perspectives: ReadonlyArray<PerspectiveAverageEntry>,
): number | null => {
  const values = nonSelfEntries(perspectives)
    .map((entry) => entry.average)
    .filter((value): value is number => typeof value === 'number')

  if (values.length === 0) {
    return null
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length
}

/** Max pairwise absolute gap among non-self averages with data. */
export const maxNonSelfDeviation = (
  perspectives: ReadonlyArray<PerspectiveAverageEntry>,
): number => {
  const values = nonSelfEntries(perspectives)
    .map((entry) => entry.average)
    .filter((value): value is number => typeof value === 'number')

  if (values.length < 2) {
    return 0
  }

  let maxGap = 0
  for (let i = 0; i < values.length; i++) {
    for (let j = i + 1; j < values.length; j++) {
      maxGap = Math.max(maxGap, Math.abs(values[i]! - values[j]!))
    }
  }
  return maxGap
}

export const selfAverage = (
  perspectives: ReadonlyArray<PerspectiveAverageEntry>,
): number | null => {
  return selfEntry(perspectives)?.average ?? null
}
